/**
 * Image Optimization Utilities
 * Optimizes images for faster loading with multi-tier support
 */

export type ImageSize = 'thumbnail' | 'preview' | 'full' | 'hero';

/**
 * Gets optimized image URL based on context
 * @param url Original image URL (preview: 1500px for Supabase, or Unsplash URL)
 * @param size Image size context: 
 *   - thumbnail: 400px (grid gallery) → uses thumbnailUrl if available
 *   - preview: 600px (scroll gallery) → uses thumbnailUrl if available
 *   - full: 1080px (dialog/fullscreen) → uses full preview URL
 *   - hero: 1920px (hero background) → uses full preview URL
 * @param thumbnailUrl Optional smaller thumbnail URL (800px) from Supabase
 */
export function getOptimizedImageUrl(url: string, size: ImageSize = 'preview', thumbnailUrl?: string): string {
  // For smaller sizes, use the thumbnail URL if available (Supabase 800px version)
  if (thumbnailUrl && (size === 'thumbnail' || size === 'preview')) {
    return thumbnailUrl;
  }

  // If not Unsplash, return original (already compressed Supabase preview)
  if (!url.includes('unsplash.com')) {
    return url;
  }

  // Define width for each size (Unsplash URL parameter)
  const widths = {
    thumbnail: 400,
    preview: 600,
    full: 1080,
    hero: 1920
  };

  const width = widths[size];

  // Replace width parameter in URL
  const optimizedUrl = url.replace(/w=\d+/, `w=${width}`);
  
  return optimizedUrl;
}

/**
 * Preloads full resolution image in background
 * Useful for smoother dialog transitions
 */
export function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
}
